import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

// Compact, non-blocking Professional Verification onboarding surface.
// Shows ONLY for professionals who have verified email but are not yet professionally verified.
export const VerificationBanner = () => {
  const { user } = useAuth();
  if (!user) return null;
  const isConsumer = user.account_type === "CONSUMER";
  const isAdmin = user.role === "ADMIN";
  const emailVerified = user.email_verified !== false; // grandfathered/absent => true
  const status = user.verification_status;
  if (isConsumer || isAdmin || !emailVerified) return null;
  if (status === "PROFESSIONAL_VERIFIED") return null;

  const pending = status === "PROFESSIONAL_VERIFICATION_PENDING";
  const rejected = status === "VERIFICATION_REJECTED";
  const reason = user.verification?.reason;
  const surface = pending ? "border-info/30 bg-info-soft" : rejected ? "border-critical/30 bg-critical-soft" : "border-attention/40 bg-attention-soft";
  const tone = pending ? "text-info" : rejected ? "text-critical" : "text-attention";

  return (
    <div
      data-testid="verification-banner"
      className={`rounded-xl border px-4 py-3 flex items-start sm:items-center gap-3 ${surface}`}
    >
      {pending ? (
        <Clock className={`h-5 w-5 ${tone} shrink-0 mt-0.5 sm:mt-0`} />
      ) : (
        <AlertTriangle className={`h-5 w-5 ${tone} shrink-0 mt-0.5 sm:mt-0`} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500">
          <span className="inline-flex items-center gap-1 text-success"><CheckCircle2 className="h-3.5 w-3.5" /> Email verified</span>
          <span>·</span>
          <span className={tone}>
            {pending ? "Professional verification — under review" : "Professional verification — action required"}
          </span>
        </div>
        <p className="text-sm text-slate-700 mt-0.5 leading-snug">
          {pending
            ? "Your professional status is being reviewed. We'll email you as soon as it's approved."
            : status === "VERIFICATION_REJECTED"
              ? "A quick correction is needed to complete your verification and publish to the Public Marketplace."
              : "Get professionally verified to establish your status and publish listings to the Public Marketplace."}
        </p>
        {!pending && reason && (
          <p data-testid="verification-reason" className={`text-xs ${tone} mt-1`}>
            <span className="font-semibold">Reviewer note:</span> {reason}
          </p>
        )}
      </div>
      {!pending && (
        <Link
          to="/profile"
          data-testid="verification-banner-cta"
          className="shrink-0 inline-flex items-center gap-1.5 bg-accent text-accent-foreground font-semibold text-sm px-3.5 py-2 rounded-lg hover:bg-accent/90 active:scale-95 transition-all"
        >
          <ShieldCheck className="h-4 w-4" />
          {status === "VERIFICATION_REJECTED" ? "Update & Resubmit" : "Get Verified"}
        </Link>
      )}
    </div>
  );
};

export default VerificationBanner;
